import { PageLayout } from 'src/layouts/page'
import { formatDate, PendingBookingProfileBanner } from '$components'
import { BinIcon, EditIcon, SmileIcon } from '$components'
import { ElementIntent } from '$components'
import { Breadcrumb } from '$components'
import { LinkButton, Button, Tabs, Tooltip, Card } from '$components'
import { Text } from '$components'
import { Skeleton } from '$components'
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { clientDeleteMutation, clientShowOptions } from '$api-client/@tanstack/react-query.gen'
import { ClientRoutePath } from '$features/clients/routes/protected'
import { OptionsDropdown } from '$components'
import { ConfirmationModal } from '$components'
import { useState } from 'react'
import { Router } from '$utils/router'
import { ImageCircle } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { BreadcrumbSkeleton } from '$components'
import { ProfileSkeleton } from '$components'
import { TabsSkeleton } from '$components'
import { InfoSkeleton } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { usePageTitle } from '$components'
import { ClientsFilterParam } from '$features/clients/enums/filter-params'

export type ClientDeleteConfirmationModalState = {
  isOpen: boolean,
  clientId: number | null,
}

export function ClientPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasSubscriptionExpired } = useTenant()

  const activeTab = location.pathname.split('/').pop()

  const [clientDeleteConfirmationModalState, setClientDeleteConfirmationModalState] = useState<ClientDeleteConfirmationModalState>({
    isOpen: false,
    clientId: null,
  })

  const { data, isLoading: isClientLoading } = useQuery({
    ...clientShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
        [ClientsFilterParam.IncludeUnapproved]: searchParams.get(ClientsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: deleteClient, isPending: isDeletingClient } = useMutation({
    ...clientDeleteMutation(),
    onSuccess: () => {
      ToastHelper.deleteSuccess(ToastType.Client)
      navigate(ClientRoutePath.Clients)
    },
    onError: () => ToastHelper.deleteError(ToastType.Client),
  })

  const client = data?.data

  usePageTitle(client ? `${client.first_name} ${client.last_name}` : 'Client')

  if (isClientLoading) {
    return (
      <PageLayout>
        <BreadcrumbSkeleton />

        <div className="flex flex-col gap-6 w-full mt-4">
          {/* Header Skeleton */}
          <div className="flex items-center gap-4">
            <ProfileSkeleton
              avatarSize={64}
              titleWidth={160}
              subtitleWidth={120}
            />
            <div className="flex items-center gap-2 ml-auto">
              <Skeleton
                width={140}
                height={36}
                borderRadius={10}
              />
              <Skeleton
                width={36}
                height={36}
                borderRadius={10}
              />
            </div>
          </div>

          {/* Tabs Skeleton */}
          <TabsSkeleton
            count={3}
            widths={[100, 100, 160]}
          />

          {/* Content Skeleton */}
          <div className="flex flex-col gap-6">
            {/* Client Information Card Skeleton */}
            <Card
              rounding="xl"
              padding="none"
            >
              <Card.Header
                padding="sm"
                className="flex justify-between items-center"
              >
                <Skeleton
                  width={160}
                  height={20}
                  borderRadius={7}
                />
                <Skeleton
                  width={64}
                  height={28}
                  borderRadius={8}
                />
              </Card.Header>

              <Card.Content
                padding="sm"
                className="flex flex-col gap-4"
              >
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
              </Card.Content>
            </Card>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!client) {
    return null
  }

  const handleConfirmDelete = () => {
    deleteClient({
      path: {
        id: Number(id),
      },
    })
  }

  return (
    <PageLayout>
      {/* Main Content */}
      <div className="flex flex-col gap-6 w-full">
        <Breadcrumb
          items={[
            {
              label: 'Clients',
              path: ClientRoutePath.Clients,
              icon: SmileIcon,
            },
            {
              label: `${client.first_name} ${client.last_name}`,
              path: ClientRoutePath.ClientsView,
            },
          ]}
        />

        {!client.approved_at && <PendingBookingProfileBanner />}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <ImageCircle
              image={client.image}
              outline="neutral"
              size="2xl"
            />

            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <Text
                  as="h1"
                  type="h2"
                >
                  {`${client.first_name} ${client.last_name}`}
                </Text>

                {
                  client.updated_at && (
                    <Text
                      type="s"
                      className="text-gray-600"
                    >
                      Last updated on
                      &nbsp;
                      {formatDate(client.updated_at!)}
                    </Text>
                  )
                }
              </div>

              {client.approved_at && (
                <Tooltip
                  content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                  disabled={!hasSubscriptionExpired}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(Router.buildRoute(
                      activeTab!,
                      ClientRoutePath.ClientsProfileEdit,
                    ))}
                    icon={EditIcon}
                    disabled={hasSubscriptionExpired}
                  >
                    Edit
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:ml-auto">
            {client.approved_at && (
              <>
                <Tooltip
                  content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                  disabled={!hasSubscriptionExpired}
                >
                  <LinkButton
                    variant="secondary"
                    to={Router.buildRoute(activeTab!, ClientRoutePath.ClientsCreateAppointment)}
                    className="w-full sm:w-fit"
                    state={{ client }}
                    disabled={hasSubscriptionExpired}
                  >
                    Schedule Appointment
                  </LinkButton>
                </Tooltip>


                <Tooltip
                  content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                  disabled={!hasSubscriptionExpired}
                >
                  <LinkButton
                    variant="secondary"
                    to={Router.buildRoute(
                      activeTab!,
                      ClientRoutePath.ClientsPetsCreate,
                    )}
                    state={{ client }}
                    className="w-full sm:w-fit"
                    disabled={hasSubscriptionExpired}
                  >
                    New Pet
                  </LinkButton>
                </Tooltip>

                <OptionsDropdown options={[
                  {
                    id: 'delete',
                    value: 'Delete',
                    onClick: () => setClientDeleteConfirmationModalState({ isOpen: true, clientId: client.id }),
                    intent: ElementIntent.Danger,
                    icon: BinIcon,
                    disabled: !!client.deleted_at || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
                    tooltip: client.deleted_at
                      ? 'This client has already been deleted.'
                      : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                        ? 'You are not allowed to delete clients.'
                        : undefined,
                  },
                ]}
                />
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs variant="pill">
          <Tabs.Tab to={ClientRoutePath.ClientsDetails}>
            Details
          </Tabs.Tab>

          <Tabs.Tab to={ClientRoutePath.ClientsPets}>
            Pets
          </Tabs.Tab>

          <Tabs.Tab to={ClientRoutePath.ClientsAppointmentHistory}>
            Appointment History
          </Tabs.Tab>
        </Tabs>

        <div>
          <Outlet />
        </div>
      </div>

      {clientDeleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeletingClient}
          intent={ElementIntent.Danger}
          onClose={() => setClientDeleteConfirmationModalState({ isOpen: false, clientId: null })}
          onConfirm={handleConfirmDelete}
          title="Delete Client"
          description="Are you sure you want to delete this client?"
        />
      )}
    </PageLayout>
  )
}
